import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import { ServicesDoneChecklist, blankSet } from "./ServicesDoneChecklist";
import { fmtDate, fmtNum, useServiceMasters } from "./shared";

const WARNING = "This reading is lower than the last recorded service — check before saving";
const today = () => new Date().toISOString().slice(0, 10);

const initSel = (entry) => {
  const sel = {};
  (entry?.lines || []).forEach((l) => {
    const set = blankSet({ grade_id: l.grade_id, quantity: String(l.quantity), part_code_id: l.part_code_id || "", legacy_text: l.part_code_id ? "" : l.part_code || "" });
    sel[l.service_type_id] = [...(sel[l.service_type_id] || []), set];
  });
  return sel;
};

function refText(r) {
  return r.kind === "last_service" ? `Last service HMR: ${fmtNum(r.hmr)} on ${fmtDate(r.date).replace(/ \d{4}$/, "")}` : `Current Hours Run: ${fmtNum(r.hmr)}`;
}

export default function EntryFormDialog({ entry, onClose, onSaved }) {
  const { types: activeTypes, grades, parts: activeParts, reload } = useServiceMasters();
  const [assets, setAssets] = useState([]);
  const [form, setForm] = useState(entry
    ? { asset_id: entry.asset_id, date: entry.date.slice(0, 10), hmr: String(entry.hmr), performed_by: entry.performed_by || "", remarks: entry.remarks || "" }
    : { asset_id: "", date: today(), hmr: "", performed_by: "", remarks: "" });
  const [sel, setSel] = useState(() => initSel(entry));
  const [errors, setErrors] = useState({});
  const [listError, setListError] = useState("");
  const [hmrTouched, setHmrTouched] = useState(false);
  const [warning, setWarning] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const inactiveTypes = (entry?.lines || [])
    .filter((l, i, a) => !activeTypes.some((t) => t.id === l.service_type_id) && a.findIndex((x) => x.service_type_id === l.service_type_id) === i)
    .map((l) => ({ id: l.service_type_id, name: `${l.service_type} (inactive)` }));
  const types = activeTypes.length ? [...activeTypes, ...inactiveTypes] : activeTypes;
  const parts = [...activeParts, ...(entry?.lines || [])
    .filter((l, i, a) => l.part_code_id && !activeParts.some((p) => p.id === l.part_code_id) && a.findIndex((x) => x.part_code_id === l.part_code_id) === i)
    .map((l) => ({ id: l.part_code_id, name: `${l.part_code} (inactive)` }))];

  useEffect(() => {
    client.get("/service-tracker/assets").then((r) => setAssets(r.data)).catch((e) => toast.error(getErrorMessage(e)));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const check = async (f = form) => {
    if (!f.asset_id || f.hmr === "" || Number.isNaN(Number(f.hmr))) {
      setWarning(null);
      return null;
    }
    try {
      const res = await client.get("/service-tracker/hmr-check", { params: { asset_id: f.asset_id, hmr: Number(f.hmr), date: f.date, exclude_id: entry?.id } });
      setWarning(res.data.warn ? res.data.references : null);
      return res.data.warn ? res.data.references : null;
    } catch (e) {
      toast.error(getErrorMessage(e));
      return null;
    }
  };

  useEffect(() => {
    if (hmrTouched) check();
  }, [form.asset_id, form.date]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickedLines = () => types.filter((t) => sel[t.id]).flatMap((t) => sel[t.id].map((s) => ({ type: t, s })));

  const validate = () => {
    const errs = {};
    const picked = pickedLines();
    const listErr = picked.length === 0 ? "Tick at least one service" : "";
    picked.forEach(({ s }) => {
      const e = {};
      if (!s.grade_id) e.grade = "Grade is required";
      if (!(Number(s.quantity) > 0)) e.quantity = "Quantity used is required";
      if (e.grade || e.quantity) errs[s.key] = e;
    });
    setErrors(errs);
    setListError(listErr);
    if (!form.asset_id) return "Asset is required";
    if (!form.date) return "Date is required";
    if (form.hmr === "" || Number(form.hmr) < 0) return "HMR at service is required";
    if (listErr) return listErr;
    if (Object.keys(errs).length) return "Fill Grade and Quantity used for every ticked service";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return toast.error(err);
    const refs = await check();
    if (refs) setConfirmOpen(true);
    else await doSave();
  };

  const doSave = async () => {
    setConfirmOpen(false);
    setSaving(true);
    try {
      const body = {
        asset_id: form.asset_id, date: form.date, hmr: Number(form.hmr),
        performed_by: form.performed_by || null, remarks: form.remarks || null,
        lines: pickedLines().map(({ type, s }) => ({ service_type_id: type.id, grade_id: s.grade_id, quantity: Number(s.quantity), part_code_id: s.part_code_id || null, part_code: s.legacy_text || null })),
      };
      if (entry) await client.put(`/service-tracker/entries/${entry.id}`, body);
      else await client.post("/service-tracker/entries", body);
      toast.success(entry ? "Service entry updated" : "Service entry saved");
      onSaved();
    } catch (er) {
      toast.error(getErrorMessage(er));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="service-entry-form-dialog">
          <DialogHeader><DialogTitle>{entry ? "Edit Service Entry" : "New Service Entry"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Asset *</label>
                <SearchableSelect testId="service-entry-asset-select" value={form.asset_id} onValueChange={(v) => set("asset_id", v)} placeholder="Select Piling rig"
                  options={assets.map((a) => ({ value: a.asset_id, label: a.asset_label, testId: `service-entry-asset-option-${a.asset_id}` }))} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Date *</label>
                <Input type="date" data-testid="service-entry-date-input" value={form.date} onChange={(e) => set("date", e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">HMR at service *</label>
                <Input type="number" min="0" step="any" data-testid="service-entry-hmr-input" value={form.hmr}
                  onChange={(e) => set("hmr", e.target.value)} onBlur={() => { setHmrTouched(true); check(); }} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Performed By</label>
                <Input data-testid="service-entry-performed-by-input" value={form.performed_by} onChange={(e) => set("performed_by", e.target.value)} />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Remarks</label>
                <Textarea data-testid="service-entry-remarks-input" value={form.remarks} onChange={(e) => set("remarks", e.target.value)} />
              </div>
            </div>

            {warning && (
              <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="service-entry-hmr-warning">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">{WARNING}</p>
                  {warning.map((r) => <p key={r.kind} className="text-xs mt-0.5" data-testid={`service-entry-hmr-warning-ref-${r.kind}`}>{refText(r)}</p>)}
                </div>
              </div>
            )}

            <ServicesDoneChecklist types={types} grades={grades} parts={parts} sel={sel} setSel={setSel} errors={errors} listError={listError} reload={reload} />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} data-testid="service-entry-cancel-button">Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="service-entry-save-button" className="bg-amber-600 hover:bg-amber-700">{saving ? "Saving..." : entry ? "Save Changes" : "Save Entry"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="service-entry-hmr-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Check this reading</AlertDialogTitle>
            <AlertDialogDescription>
              {WARNING}.
              {(warning || []).map((r) => <span key={r.kind} className="block mt-1">{refText(r)}</span>)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="service-entry-hmr-confirm-back">Go back</AlertDialogCancel>
            <AlertDialogAction data-testid="service-entry-hmr-confirm-save" className="bg-amber-600 hover:bg-amber-700" onClick={doSave}>Save anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
